<template>
  <b-modal
    :modal-class="'app-modal-form' + (unclosable ? ' unclosable' : '')"
    :dialog-class="'modal-' + size"
    :id="id"
    :ok-title="okTitle"
    :cancel-title="cancelTitle"
    no-close-on-backdrop
    :body-class="{ squash }"
    @close="onClose"
    :no-close-on-esc="preventClosingWithEscape"
  >
    <template v-slot:modal-title>
      <div class="app-modal-form-title">
        <slot name="modal-title">
          <h2>{{ title }} <app-super-admin-marker v-if="superAdminProtected" /></h2>
          <div v-if="subtitle" v-html="subtitle" class="app-modal-form-title-subtitle text-muted"></div>
        </slot>
      </div>
    </template>
    <app-task-progress :task="task" v-if="task" class="mar-bottom" />
    <app-alert
      v-show="showAlert"
      :variant="alertVariant"
      class="app-modal-form-alert"
      :dismissible="true"
    >
      <div v-html="alertMsg"></div>
    </app-alert>
    <form @submit.prevent="onSubmit">
      <slot></slot>
    </form>
    <template v-slot:modal-footer>
      <slot name="modal-foot" />

      <app-button v-if="showCancelButton" variant="secondary" @click="onCancel">{{ innerCancelTitle }}</app-button>
      <app-button v-if="!hideSubmitButton" :variant="submitButtonVariant" @click="onSubmitButtonClick" :disabled="submitButtonDisabled" :loading="modalLoading" :long-loading-time="longLoadingTime">{{ okTitle }}</app-button>
    </template>
  </b-modal>
</template>

<script lang="ts">
import { Component, Prop, Watch } from "vue-property-decorator";
import { IAppModalForm } from "@/app/shared/components/app-modal/types";
import AppButton from "@/app/shared/components/app-button/app-button.vue";
import { BaseAuthComponent } from "../base-auth-component/base-auth-component";
import AppSuperAdminMarker from "@/app/shared/components/app-super-admin-marker/app-super-admin-marker.vue";
import AppAlert from "../app-alert/app-alert.vue";
import AppTaskProgress from "../app-task-progress/app-task-progress.vue";
import { TaskSchema } from "../../services/volateq-api/api-schemas/task-schema";

@Component({
  name: "app-modal-form",
  components: {
    AppButton,
    AppSuperAdminMarker,
    AppAlert,
    AppTaskProgress,
  },
})
export default class AppModalForm extends BaseAuthComponent implements IAppModalForm {
  @Prop({ required: true }) id!: string;
  @Prop({ default: "" }) title!: string;
  @Prop({ default: null }) subtitle!: string | null;
  @Prop({ default: "ok" }) okTitle!: string;
  @Prop({ default: "primary" }) submitButtonVariant!: string;
  @Prop({ default: "" }) cancelTitle!: string; 
  @Prop({ default: false }) modalLoading!: boolean;
  @Prop({ default: false }) superAdminProtected!: boolean;
  @Prop({ default: true }) showCancelButton!: boolean
  @Prop({ default: "md" }) size!: "md" | "lg" | "xl";
  @Prop({ default: false }) submitOnButtonClickOnly!: boolean;
  @Prop({ default: false }) squash!: boolean;
  @Prop({ default: false }) submitButtonDisabled!: boolean;
  @Prop({ default: false }) hideSubmitButton!: boolean;
  @Prop({ default: false }) preventClosingWithEscape!: boolean;
  @Prop({ default: false }) unclosable!: boolean;
  @Prop({ default: false }) longLoadingTime!: boolean;

  showAlert = false;
  alertMsg = "";
  alertVariant = "default";

  innerCancelTitle = this.$t("cancel").toString();

  task: TaskSchema | null = null;

  @Watch("cancelTitle", { immediate: true })
  onCancelTitleChanged() {
    this.innerCancelTitle = this.cancelTitle;
  }

  show() {
    this.$bvModal.show(this.id || "");
  }

  hide() {
    this.hideAlert();
    this.$bvModal.hide(this.id || "");
  }

  alertError(msg: string | any) {
    this.alertVariant = "danger";
    this.showAlert = true;

    console.error(msg);

    if (typeof msg === "string") {
      this.alertMsg = msg;
    } else {
      this.alertMsg = msg.error;
      const details = msg.details || msg.message;
      if (details) {
        this.alertMsg += "<br><small>Details: " + details + "</small>";
      }
    }
  }

  alertInfo(msg: string) {
    this.alertVariant = "info";
    this.showAlert = true;
    this.alertMsg = msg;
  }

  showTask(task: TaskSchema) {
    this.task = task;
  }

  hideTask() {
    this.task = null;
  }

  onSubmit(e: Event) {
    if (!this.submitOnButtonClickOnly) {
      this.$emit("submit");
    }
  }

  onSubmitButtonClick(e: Event) {
    this.$emit("submit");
  }

  onCancel(e: Event) {
    this.$emit("cancel");

    this.hide();
  }

  onClose() {
    this.$emit("cancel");
  }

  hideAlert() {
    this.showAlert = false;
    this.alertMsg = "";
  }
}
</script>

<style lang="scss">
@import "@/scss/_colors.scss";
.modal-body.squash {
  padding-top: 0;
  padding-bottom: 0;

  .app-modal-form-info-area  {
    padding-top: 1em;
    padding-bottom: 1em;
  }
}

.modal-content {
  background-color: var(--default-bg-color);
}

.modal-header {
  .close {
    color: var(--muted-text-color);
    text-shadow: none;
  }
}

.app-modal-form {  
  &.unclosable .modal-header .close {
    display: none;
  }

  .modal-header {
    align-items: flex-start;
  }

  .modal-title {
    flex: 1 1 auto;
    min-width: 0;
    margin-right: 0.5rem;
  }

  .close {
    flex-shrink: 0;
  }

  &-title {
    h2 {
      font-size: 1.3em;
      margin-bottom: 0.5em;
      max-width: 100%;
      overflow-wrap: anywhere;
      word-break: break-word;
    }

    &-subtitle {
      font-size: 1em;
      margin-bottom: 0.5em;
    }
  }
  &-alert {
    margin-bottom: 1.5rem;
  }
}

@media (max-width: 767.98px) {
  .app-modal-form .modal-dialog {
    max-width: calc(100vw - 1rem);
  }
}
</style>
